package com.nonopn.skywatcher.common.app.pages.map.view.alerts

import androidx.compose.foundation.background
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material.Divider
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.nonopn.skywatcher.app.utils.round
import com.nonopn.skywatcher.common.app.home.HotPreviewApp
import com.nonopn.skywatcher.common.app.pages.map.model.FakeMainScreenModel
import com.nonopn.skywatcher.common.app.pages.map.model.MainScreenModel
import com.nonopn.skywatcher.common.app.pages.map.model.weather.EtaHolder
import com.nonopn.skywatcher.common.app.pages.map.model.weather.ProbeStatus
import com.nonopn.skywatcher.common.app.pages.map.model.weather.WeatherReport
import com.nonopn.skywatcher.common.app.pages.map.view.views.CustomVerticalDivider
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.alert_banner_closest
import com.nonopn.skywatcher.common.app.res.alert_banner_strongest
import com.nonopn.skywatcher.design.local.LocalFontSizes
import com.nonopn.skywatcher.design.theme.AppColor
import com.nonopn.skywatcher.design.utils.WindowType
import com.nonopn.skywatcher.design.utils.localized
import de.drick.compose.hotpreview.HotPreview
import eu.codlab.compose.theme.LocalDarkTheme
import eu.codlab.compose.widgets.TextNormal
import korlibs.memory.divRound
import org.jetbrains.compose.resources.StringResource
import kotlin.math.roundToInt

@Composable
fun ShowExpectedData(
    modifier: Modifier,
    model: MainScreenModel,
    eta: EtaHolder
) {
    val states by model.states.collectAsState()

    Column(
        modifier,
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally
    ) {

        TextNormal(
            text = if (eta.overhead) {
                "${eta.avgKPH.round(2)}Km/h"
            } else {
                "~${eta.avgKM.round(2)}Km / ${eta.avgKPH.round(2)}Km/h"
            },
            fontSize = LocalFontSizes.current.map.alertBannerSub,
            fontWeight = FontWeight.Bold,
            textAlign = TextAlign.Center
        )

        Divider(
            Modifier.padding(vertical = 8.dp).widthIn(0.dp, 150.dp).background(dividerColor()),
            thickness = 0.dp
        )

        states.lightningActivityInfo?.let { info ->
            Row(
                horizontalArrangement = Arrangement.spacedBy(32.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                ShowLightningInfo(
                    Res.string.alert_banner_closest,
                    info.closestDistance,
                    info.closest.pulsePeakAmp
                )

                CustomVerticalDivider(
                    50.dp
                )

                ShowLightningInfo(
                    Res.string.alert_banner_strongest,
                    info.strongestDistance,
                    info.strongest.pulsePeakAmp
                )
            }
        }
    }
}

@Composable
private fun ShowLightningInfo(
    text: StringResource,
    distance: Double,
    pulseAmp: Int
) {
    Column(
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        TextNormal(
            modifier = Modifier.padding(bottom = 8.dp),
            text = text.localized(),
            fontSize = LocalFontSizes.current.map.alertBannerSub,
            fontWeight = FontWeight.Bold,
            textAlign = TextAlign.Center
        )

        TextNormal(
            modifier = Modifier.padding(bottom = 8.dp),
            text = "${distance.roundToInt()}km",
            fontSize = LocalFontSizes.current.map.alertBannerTitle,
            fontWeight = FontWeight.Bold,
            textAlign = TextAlign.Center
        )

        TextNormal(
            text = "${pulseAmp.divRound(1000)}kA",
            fontSize = LocalFontSizes.current.map.alertBannerTitle,
            fontWeight = FontWeight.Bold,
            textAlign = TextAlign.Center
        )
    }
}

@Composable
fun dividerColor(override: Color? = null) = override ?: when (LocalDarkTheme.current) {
    true -> AppColor.White
    false -> AppColor.GrayExtraDark
}

/*
@Composable
fun ShowExpectedData(
    modifier: Modifier,
    overridingStorm: Storm,
    lightningActivity: List<ThunderDetectionActivity>
) {
    Column(
        modifier,
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        listOf(
            listOf(
                "${Res.string.alert_banner_average.localized()} ${overridingStorm.distance.avgKM}Km",
                "${overridingStorm.speed.avgKPH}Km/h"
            ),
            listOf(
                "${Res.string.alert_banner_minimum.localized()} ${overridingStorm.distance.minKM}Km",
                "${overridingStorm.speed.minKPH}Km/h"
            ),
            listOf(
                "${Res.string.alert_banner_maximum.localized()} ${overridingStorm.distance.maxKM}Km",
                "${overridingStorm.speed.maxKPH}Km/h"
            ),
        ).forEach { (left, right) ->
            Row(
                horizontalArrangement = Arrangement.spacedBy(32.dp)
            ) {
                TextNormal(
                    text = left,
                    fontSize = LocalFontSizes.current.map.alertBannerSub,
                    fontWeight = FontWeight.Bold
                )

                TextNormal(
                    text = right,
                    fontSize = LocalFontSizes.current.map.alertBannerSub,
                    fontWeight = FontWeight.Bold
                )
            }
        }
    }
}
*/

@HotPreview(widthDp = 400, heightDp = 150, darkMode = true)
@HotPreview(widthDp = 400, heightDp = 150, darkMode = false)
@Composable
private fun ShowExpectedDataPreview() {
    HotPreviewApp(
        Modifier.fillMaxSize(),
        isDarkTheme = isSystemInDarkTheme(),
        isPreview = true,
        frameType = WindowType.SMARTPHONE_TINY
    ) {
        ShowExpectedData(
            Modifier.fillMaxWidth().height(150.dp),
            FakeMainScreenModel(
                fakeProbeStatus = ProbeStatus(
                    report = WeatherReport.fake()
                ),
            ),
            EtaHolder.fake
        )
    }
}
